import React, { useState, useRef, useEffect } from "react";
import { Home, Send, Loader2, Sparkles } from "lucide-react";
import { askHmo } from "../api";

const SUGGESTIONS = [
  "Would this property need an HMO licence?",
  "Is this area under an Article 4 Direction?",
  "What's the difference between C3, C4 and sui generis?",
  "How do I check if a nearby property is a licensed HMO?",
];

const HMOHelper = ({ property }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (question) => {
    const q = (question || "").trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await askHmo({ question: q, sessionId, propertyId: property?.id, address: property?.address, postcode: property?.postcode });
      setSessionId(res.sessionId);
      setMessages((m) => [...m, { role: "assistant", text: res.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — the assistant is busy right now. Please try again in a moment." }]);
    }
    setLoading(false);
  };

  return (
    <div className="py-8 border-t border-[#e6ddd0]" data-testid="hmo-helper">
      <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-2 flex items-center gap-2">
        <Home size={20} className="text-[#b8935f]" /> HMO Helper
      </h2>
      <p className="text-[14px] text-[#6a6a6a] leading-relaxed mb-5">
        Ask about HMO licensing, Article 4 and planning for this property's area. Informational guidance only — always confirm with the local council's HMO register.
      </p>

      <div className="rounded-2xl border border-[#f0ece5] bg-[#faf7f2] overflow-hidden">
        <div className="max-h-[360px] overflow-y-auto p-4 space-y-3" data-testid="hmo-messages">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <span className="inline-flex w-11 h-11 rounded-full bg-[#b8935f]/15 items-center justify-center text-[#b8935f] mb-3"><Sparkles size={20} /></span>
              <p className="text-[13px] text-[#8a8a8a] mb-4">Try one of these to get started:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="hmo-suggestion" className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#b8935f] text-white rounded-br-md" : "bg-white border border-[#f0ece5] text-[#3a3a3a] rounded-bl-md"}`}>{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start"><div className="px-4 py-2.5 rounded-2xl bg-white border border-[#f0ece5] text-[#8a8a8a] text-[13px] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Thinking…</div></div>
          )}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex items-center gap-2 p-3 border-t border-[#f0ece5] bg-white">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about HMOs near this property…" data-testid="hmo-input" className="flex-1 h-[44px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          <button type="submit" disabled={loading || !input.trim()} data-testid="hmo-send" className="w-[44px] h-[44px] shrink-0 rounded-lg bg-[#b8935f] text-white grid place-items-center hover:bg-[#a67f4d] transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default HMOHelper;
